import React, { useEffect, useState } from 'react';
import { FiSave, FiRefreshCw, FiUpload } from 'react-icons/fi';
import API from '../utils/api';
import { toast } from 'react-toastify';
import './AdminPages.css';

const SECTIONS = [
  { key: 'hero',     label: '🖼️  Hero Section' },
  { key: 'stats',    label: '📊 Statistics' },
  { key: 'about',    label: '📖 About Section' },
  { key: 'contact',  label: '📞 Contact Info' },
  { key: 'whatsapp', label: '💬 WhatsApp' },
  { key: 'social',   label: '🔗 Social Links' },
  { key: 'seo',      label: '🔍 SEO & Meta' },
];

export default function AdminSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [localVals, setLocalVals] = useState({});

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const r = await API.get('/api/admin/settings');
      setSettings(r.data.data || {});
      // Initialize local editable values
      const init = {};
      Object.entries(r.data.data || {}).forEach(([k, v]) => { init[k] = v.value; });
      setLocalVals(init);
    } catch { toast.error('Failed to load settings'); }
    setLoading(false);
  };
  useEffect(() => { fetchSettings(); }, []);

  const handleChange = (key, value) => setLocalVals(prev => ({ ...prev, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const updates = {};
      Object.entries(settings).forEach(([k, v]) => {
        updates[k] = { value: localVals[k], label: v.label, type: v.type, section: v.section };
      });
      await API.put('/api/admin/settings', updates);
      toast.success('Settings saved successfully!');
      fetchSettings();
    } catch { toast.error('Save failed'); }
    setSaving(false);
  };

  const handleImageUpload = async (key, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('image', file);
    fd.append('key', key);
    fd.append('label', settings[key]?.label || key);
    fd.append('section', settings[key]?.section || 'media');
    try {
      const r = await API.post('/api/admin/settings/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      handleChange(key, r.data.url);
      toast.success('Image uploaded!');
    } catch { toast.error('Upload failed'); }
  };

  // Get all settings for a section
  const sectionSettings = Object.entries(settings).filter(([, v]) => v.section === activeSection);

  if (loading) return <div className="spinner" style={{ margin: '80px auto' }} />;

  return (
    <div>
      <div className="admin-page-header">
        <div><h2>Site Settings</h2><p>Edit all website content from here</p></div>
        <button className="btn-admin-primary" onClick={handleSave} disabled={saving}>
          {saving ? <><FiRefreshCw style={{ animation:'spin 0.8s linear infinite' }} /> Saving...</> : <><FiSave /> Save All Changes</>}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: 24 }}>
        {/* Section nav */}
        <div className="admin-card" style={{ height: 'fit-content' }}>
          <div className="admin-card-body padded" style={{ padding: '12px 8px' }}>
            {SECTIONS.map(s => (
              <button
                key={s.key}
                onClick={() => setActiveSection(s.key)}
                style={{
                  width: '100%', padding: '10px 14px', border: 'none', borderRadius: 10,
                  background: activeSection === s.key ? 'rgba(26,71,42,0.1)' : 'transparent',
                  color: activeSection === s.key ? 'var(--primary)' : 'var(--text-dark)',
                  fontWeight: activeSection === s.key ? 600 : 400,
                  cursor: 'pointer', textAlign: 'left', fontSize: '0.88rem',
                  fontFamily: 'var(--font-body)', transition: 'all 0.2s',
                  marginBottom: 2,
                  borderLeft: activeSection === s.key ? '3px solid var(--primary)' : '3px solid transparent',
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Settings form */}
        <div className="admin-card">
          <div className="admin-card-header">
            <h3>{SECTIONS.find(s => s.key === activeSection)?.label}</h3>
          </div>
          <div className="admin-card-body padded">
            {sectionSettings.length === 0 ? (
              <div className="admin-empty">No settings in this section yet.</div>
            ) : (
              <div className="admin-form">
                {sectionSettings.map(([key, setting]) => (
                  <div className="form-group" key={key}>
                    <label>{setting.label || key}</label>
                    {setting.type === 'textarea' ? (
                      <textarea rows={3} value={localVals[key] || ''} onChange={e => handleChange(key, e.target.value)} />
                    ) : setting.type === 'image' ? (
                      <div>
                        <div className="img-upload-box" style={{ marginBottom: 8 }}>
                          <input type="file" accept="image/*" onChange={e => handleImageUpload(key, e.target.files[0])} />
                          {localVals[key]
                            ? <img className="current-img" src={localVals[key]} alt={setting.label} onError={e => { e.target.src='/ladakh.jpg'; }} />
                            : <><div className="upload-icon"><FiUpload /></div><p>Click to upload image</p></>
                          }
                        </div>
                        <input value={localVals[key] || ''} onChange={e => handleChange(key, e.target.value)} placeholder="Or enter image URL" />
                      </div>
                    ) : setting.type === 'boolean' ? (
                      <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                        <input type="checkbox" checked={!!localVals[key]} onChange={e => handleChange(key, e.target.checked)} />
                        {setting.label}
                      </label>
                    ) : (
                      <input type="text" value={localVals[key] || ''} onChange={e => handleChange(key, e.target.value)} />
                    )}
                    <small style={{ color:'var(--text-light)', fontSize:'0.72rem', marginTop:3, display:'block' }}>Key: <code>{key}</code></small>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
